import { useSyncExternalStore } from 'react';
import { URLParameters } from '@/app/common/urlParameters';

/**
 * The ?modal= stack: the ids of the modals that are open, outermost first. Modals keep it in the
 * url so the browser back button closes them one at a time (see useBrowserBackButton).
 */
export function getCurrentModals() {
  // MPH-965: parse the query string, not the whole href. URLSearchParams splits on '&', so with the
  // full href the first pair keeps the origin+path glued to its key ('https://host/path?modal') and
  // the modal stack is only found by accident, when some other parameter comes first in the url.
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(URLParameters.MODAL)?.split(',') ?? [];
}

// What is left of the stack the page arrived on, for modals that were not mounted to read it
// themselves. MPH-985: the restore used to read the url whenever a provider mounted, which is how a
// modal mounted much later — the flight details inside the change-flight drawer — found an id left
// over from the load and opened on its own. Taking the stack once, and spending it, is what stops
// that; PageWrapperClient, which strips ?modal= on mount, hands it over before stripping so the
// restore does not depend on which of the two effects runs first either.
// undefined = not captured yet; [] = captured and nothing (left) to restore.
let restorableModals: string[] | undefined;
// The modal next in line that a restore has already reopened and that is not open yet: it keeps its
// turn until it is, so it is not reopened twice and the modal stacked on it cannot overtake it in
// the url. (MPH-969)
let claimedModal: string | undefined;
let watchingHistoryEntries = false;
const listeners = new Set<() => void>();

function setRestorableModals(modals: string[]) {
  restorableModals = modals;
  listeners.forEach((listener) => listener());
}

function captureFromCurrentUrl() {
  // MPH-969: the whole stack, outermost first, reopened one modal at a time: an inner modal only
  // gets its turn once the modal it was opened from is open again, and its data often depends on
  // that one — the flight details opened from the change-flight list need the list. So it waits for
  // its turn and for its data rather than being dropped, as it was when only the outermost modal
  // was kept.
  claimedModal = undefined;
  setRestorableModals(getCurrentModals());
}

export function captureInitialModalStack() {
  if (!watchingHistoryEntries) {
    watchingHistoryEntries = true;
    // Back and forward land on another history entry, which carries a stack of its own, and inside
    // the funnel that happens without a document load: PageWrapperClient swaps the page by pageType
    // (flight page <-> package review), so the page's modal providers remount with the stack of the
    // document long spent. Every entry the app lands on brings its own. (MPH-985)
    window.addEventListener('popstate', captureFromCurrentUrl);
  }
  if (restorableModals) return;
  captureFromCurrentUrl();
}

/**
 * The modal next in line to be reopened: the outermost one of the stack the page arrived on that is
 * not open again yet. Reading it claims nothing.
 */
export function getNextRestorableModal() {
  return restorableModals?.[0];
}

function subscribeRestorableModals(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * getNextRestorableModal() as state: the modals of the stack wait for their turn, so each one has to
 * hear when the modal below it has been reopened. Nothing is restorable on the server.
 */
export function useNextRestorableModal() {
  return useSyncExternalStore(subscribeRestorableModals, getNextRestorableModal, () => undefined);
}

/**
 * Claims `modalId` for a restore, if it is next in line and nobody has claimed it yet. It stays next
 * in line until it is open (settleOpenedModal).
 */
export function takeRestorableModal(modalId: string) {
  captureInitialModalStack();
  if (restorableModals?.[0] !== modalId || claimedModal === modalId) return false;
  claimedModal = modalId;
  return true;
}

/**
 * A modal is open and listed in the url. If it was next in line, the turn passes to the modal
 * stacked on top of it. Otherwise it is none of the arrival stack's business — the user opened it —
 * and nothing left of that stack may reopen on top of it.
 */
export function settleOpenedModal(modalId: string) {
  captureInitialModalStack();
  if (restorableModals?.[0] === modalId) {
    claimedModal = undefined;
    setRestorableModals(restorableModals.slice(1));
  } else {
    dropRestorableModals();
  }
}

/**
 * What `modalId` showed goes by another id now — a flight comes back under a new one once the page
 * has been priced again — so the stack names it by that one, in the same place in line.
 */
export function renameRestorableModal(modalId: string, renamedModalId: string) {
  if (modalId === renamedModalId || !restorableModals?.includes(modalId)) return;
  setRestorableModals(
    restorableModals.map((restorableModal) => (restorableModal === modalId ? renamedModalId : restorableModal))
  );
}

/**
 * A modal of the stack that cannot be reopened any more — the flight it showed is no longer on
 * offer — leaves with the modals stacked on top of it, which were opened from it.
 */
export function dropRestorableModal(modalId: string) {
  const index = restorableModals?.indexOf(modalId) ?? -1;
  if (restorableModals && index >= 0) setRestorableModals(restorableModals.slice(0, index));
}

/**
 * The stack stops being restorable as soon as the user opens a modal themselves, or closes one:
 * an inner modal cannot come back once the modal it was opened from is gone.
 */
export function dropRestorableModals() {
  claimedModal = undefined;
  setRestorableModals([]);
}

/** Tests only — the capture otherwise lives as long as the document. */
export function resetInitialModalStack() {
  restorableModals = undefined;
  claimedModal = undefined;
}
